/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import { NavigationPrompt } from 'components/Menu';
import settings from 'settings';
import moment from 'moment-timezone';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import {
  activityItemContent, blockNavigation, getCustomFilters, isNavigationBlocked, promptNavigation, toggleListSelection
} from 'library/helpers';
import {
  applySignOn, isOta, signOnToast, TASK as SIGN_ON_TASK, toUtcValue
} from 'library/availableSignOn';
import { getOtPresets } from 'library/otPresets';
import { connect } from 'react-redux';
import { history } from 'store';
import { addRosterEventDetails } from 'actions/templateMapperActions';

import {
  addTimelineActivity,
  checkAddedActivities,
  clearClipboard,
  clearLocalAssignments,
  copyAssignments,
  deleteTimelineActivity,
  loadServerAssignments,
  moveTimelineActivities,
  pasteAssignments,
  resizeTimelineActivities,
  saveLocalAssignments,
  selectTimelineActivities,
  setTimelineGroups,
  swapTimelineAssignmentIds,
  toggleGroupPin,
  updateAssignmentAction,
  updateLocalAssignments,
  updateTravelAssignments,
  updatePinnedActivities
} from 'actions/rosterActions';
import filterValidations from 'components/Validations/helper';
// `dataShapes` is used in a to-be-merged pr
/* eslint-disable no-unused-vars */
import { getAvailabilityLayers, isLeaveItem, isTravelItem } from 'selectors/roster.selectors';
import { formatTags, THEME_NAMES, unFormatTags } from 'selectors/tag.selectors';
/* eslint-enable no-unused-vars */
import KeyHandler from 'react-key-handler';
import REDUCER_KEY from 'reducers/reducer.keys';
import THEME_KEY from 'reducers/theme.keys';
import {
  add, list, load, reset, update
} from 'actions';
import HeaderBar from 'components/headerBar/header-bar';
import ToolBar from 'components/toolBar/tool-bar';
import SlidePanel from 'components/slidePanel/slide-panel';
import ValidationTasks, { Counters } from 'components/Validations';
import {
  Button, DatePicker, Drawer, Icon, Menu, message, Modal, Spin, Tabs, Tooltip
} from 'antd';

import * as rosterPivot from 'selectors/roster-pivot';
import ReassignActivities from 'components/UserAssign/ReassignActivitiesForm';
import { Notifications, UserAccount } from 'views/shared';
import {
  ActivitiesPanel, GroupRenderer, ItemRenderer, NotificationsPanel, Sidebar
} from 'components/Timeline';
import { ActionInput } from 'views/manage/Actions';
import { getTaggedItems } from 'components/Filtering/helpers';
import { AuditHistory } from 'components/Audit';
import { perfMark, initRosterNetworkObserver, markNextTwoFrames } from 'libs/perf/rosterRenderPerf';
import Timeline from '../../../../libs/timeline/timeline';
import TemplateMapper from './templateMapper/template-mapper';
import ActivityInput from './activityForm/ActivityInput';
import TravelInput from './TravelInput';
import SignOnPopover from './SignOnPopover';
import { defaultSettings, filters, pickers } from './config';

const { TabPane } = Tabs;
const noOp = () => {
};

const { TIMELINE_MODES } = Timeline;
const MIN_ZOOM_DAYS = 1;
const MAX_ZOOM_DAYS = 300;

const MOUSE_RIGHT_CLICK = 2;
const DELETE_KEY_CODE = 46;
const BACKSPACE_KEY_CODE = 8;
const CTRL_KEY_CODE = 17;
const C_KEY_CODE = 67;
const V_KEY_CODE = 86;
const MAX_USERS = 1000;

const getItem = (ids, item) => ({
  ...item,
  id: ids[item.id] || item.id // provide the swapped id
});

const mapAssignments = (item) => {
  if (isTravelItem(item)) {
    return {
      title: `${item.reference_number} - Travel ${item.travel_type}`,
      key: item.id,
      row: item.group,
      start: item.start_time,
      end: item.end_time,
      user: item.user,
      booking: item,
      notes: item.notes
    };
  }

  if (isLeaveItem(item)) {
    return {
      title: `${item.type.name} - ${item.status.status_on_timeline}`,
      key: item.id,
      row: item.group,
      start: item.start_time,
      end: item.end_time,
      leave: item
    };
  }

  return {
    ...item,
    key: item.id,
    row: item.group,
    start: moment.isMoment(item.start_time) ? item.start_time : moment(item.start_time),
    end: moment.isMoment(item.end_time) ? item.end_time : moment(item.end_time)
  };
};

const isBitSet = (bit, mask) => (bit & mask) === bit;

// Flatten roster validation tasks into per-assignment error rows. Pure and side-effect
// free so it can be memoized (keyed on the validationTasks array identity).
const computeValidationErrors = (validationTasks) => {
  const filteredValidations = filterValidations(validationTasks);
  const validations = filteredValidations.filter(item => item.status === 'Error' || item.status === 'Failed');
  return [].concat(...validations.map(validation => validation.validation_errors.map(error => ({
    ...error,
    status: validation.status,
    name: validation.validation_name,
    triggered_on: moment(validation.triggered_on)
      .format('YYYY-MM-DD HH:mm:ss')
  }))))
    .map(error => ({
      ...error,
      ...error.affected_assignment,
      key: error.id
    }));
};

const breadcrumbs = (rosterVersion) => {
  const parentRoster = rosterVersion.parent_roster || {};
  const rosterName = parentRoster.name || '';

  return (
    <div className="bhp-breadcrumb">
      <div className="bhp-item">Roster</div>
      <div className="bhp-item bhp-inverted">{rosterName}</div>
      <div className="bhp-item bhp-inverted">{rosterVersion.name}</div>
    </div>
  );
};

const mapStateToProps = state => ({
  rosterVersion: state.api.rosterVersion.data,
  activities: state.api.activities.data.results,

  user_groups: state.rosterTimeline.user_groups,
  activity_groups: state.rosterTimeline.activity_groups,
  server_items: state.rosterTimeline.server_items,
  travel_items: state.rosterTimeline.travel_items,
  leave_items: state.rosterTimeline.leave_items,
  local_items: state.rosterTimeline.local_items,
  deleted_items: state.rosterTimeline.deleted_items,
  availability_items: state.rosterTimeline.availability_items,
  selectedAssignments: state.rosterTimeline.selected,
  pinnedActivities: state.rosterTimeline.pinnedActivities,

  usersInFlight: state.api.users.inFlight,
  activityInFlight: state.api.activities.inFlight,
  // userFilterInFlight: state.api.userItemTags.inFlight,
  rosterVersionInFlight: state.api.rosterVersion.inFlight,

  notifications: state.api.notificationRuns.data.results,

  validationTasks: state.api.validationRosterTasks.data.results,
  clipboard: state.rosterTimeline.clipboard
});

const mapDispatchToProps = dispatch => ({
  loadValidationTasks(rosterId, start, end) {
    const sorted = [
      {
        id: 'triggered_on',
        desc: true
      },
      {
        id: 'validation',
        desc: true
      }
    ];
    const validationFilters = [
      {
        id: 'roster_version__id',
        value: rosterId
      },
      {
        id: 'boundary_start',
        value: start.format('YYYY-MM-DDTHH:mm:ssZ')
      },
      {
        id: 'boundary_end',
        value: end.format('YYYY-MM-DDTHH:mm:ssZ')
      }
    ];
    dispatch(list(REDUCER_KEY.validationRosterTasks.key, {
      page: 0,
      pageSize: 1000,
      filters: validationFilters,
      sorted
    }));
  },
  loadTags(id) {
    return dispatch(list(
      REDUCER_KEY.themes.key,
      {
        page: 0,
        pageSize: settings.APP_CONFIG.dropListSize,
        filters: [{
          id: 'model',
          value: 'assignment'
        }],
        sorted: [{
          id: 'model',
          desc: false
        }]
      }
    ))
      .then((response) => {
        const page = 0;
        const pageSize = settings.APP_CONFIG.dropListSize;
        const sorted = [{
          id: 'name',
          desc: false
        }];
        return dispatch(list(REDUCER_KEY.itemTags.key, {
          page,
          pageSize,
          filters: [
            {
              id: 'theme',
              value: response.results[0].id
            },
            {
              id: 'object_id',
              value: id
            }
          ],
          sorted
        }));
      });
  },
  resetActionType() {
    dispatch(reset(REDUCER_KEY.actionType.key));
  },
  loadActionContext() {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;

    dispatch(list(
      REDUCER_KEY.actionContextList.key, {
        page,
        pageSize,
        filters: [{
          id: 'active',
          value: true
        }],
        sorted: [{
          id: 'name',
          desc: false
        }]
      }
    ));
  },
  clearAction() {
    dispatch(reset(REDUCER_KEY.action.key));
  },
  saveAction(data) {
    return dispatch(add(REDUCER_KEY.action.key, data));
  },
  dispatchUpdateAssignmentAction(assignmentId, latest_action, has_unresolved_actions) {
    dispatch(updateAssignmentAction(assignmentId, latest_action, has_unresolved_actions));
  },
  checkCompetencies(itemsToCheck) {
    return dispatch(checkAddedActivities(itemsToCheck));
  },
  dispatchCopyAssignments(ids, rosterVersionId) {
    dispatch(copyAssignments(ids, rosterVersionId));
  },
  dispatchPasteAssignments(anchorRowId, anchorTime, rosterVersionId) {
    return dispatch(pasteAssignments(anchorRowId, anchorTime, rosterVersionId));
  },
  dispatchClearClipboard() {
    dispatch(clearClipboard());
  },
  loadRosterVersion(id) {
    return dispatch(load(REDUCER_KEY.rosterVersion.key, { id }));
  },
  loadUser(id) {
    return dispatch(load(
      REDUCER_KEY.userAccount.key, { id }
    ));
  },
  clearUser() {
    dispatch(reset(REDUCER_KEY.userAccount.key));
    dispatch(reset(REDUCER_KEY.userItemTags.key));
  },
  loadUserList(filtersIn = [], sorted = []) {
    return dispatch(list(
      REDUCER_KEY.users.key, {
        page: 0,
        pageSize: MAX_USERS,
        filters: filtersIn,
        sorted
      }
    ));
  },
  loadUserTags(id, startDate, endDate) {
    return dispatch(list(
      REDUCER_KEY.themes.key,
      {
        page: 0,
        pageSize: settings.APP_CONFIG.dropListSize,
        filters: [{
          id: 'model',
          value: 'user'
        }],
        sorted: [{
          id: 'model',
          desc: false
        }]
      }
    ))
      .then((response) => {
        const page = 0;
        const pageSize = settings.APP_CONFIG.dropListSize;
        const sorted = [{
          id: 'name',
          desc: false
        }];
        return dispatch(list(REDUCER_KEY.userItemTags.key, {
          page,
          pageSize,
          filters: [
            {
              id: 'theme',
              value: response.results[0].id
            },
            {
              id: 'object_id',
              value: id
            },
            {
              id: 'start_date__lte',
              value: endDate
            },
            {
              id: 'expiry_date__gte',
              value: startDate
            }
          ],
          sorted
        }));
      });
  },
  loadActivityList(filtersIn = []) {
    return dispatch(list(
      REDUCER_KEY.activities.key, {
        page: 0,
        pageSize: 1000,
        filters: filtersIn,
        sorted: [{ id: 'name' }]
      }
    ));
  },
  loadRosterServerAssignments(
    rosterVersionId,
    userIds,
    visibleTimeStart,
    visibleTimeEnd,
    displayTravelLayer,
    displayLeaveLayer,
    displayAvailabilityLayer,
    afterResultDispatch,
    shouldApplyResults,
    includeUnassigned = true
  ) {
    const layers = {
      travel: displayTravelLayer,
      leave: displayLeaveLayer,
      availability: displayAvailabilityLayer
    };
    return dispatch(loadServerAssignments(
      rosterVersionId,
      userIds,
      visibleTimeStart,
      visibleTimeEnd,
      layers,
      includeUnassigned,
      afterResultDispatch,
      shouldApplyResults
    ));
  },

  saveRosterVersion(id, parent_roster_id, name, default_settings) {
    const params = {
      id,
      parent_roster_id,
      name,
      default_settings
    };
    return dispatch(id < 0 || id === null ? add(REDUCER_KEY.rosterVersion.key, params) : update(REDUCER_KEY.rosterVersion.key, params));
  },
  saveRosterLocalAssignments(localItems, deletedItems, travelItems, rosterVersionId, start, end) {
    return dispatch(saveLocalAssignments(localItems, deletedItems, travelItems, rosterVersionId, start, end));
  },
  updateRosterLocalAssignments(items) {
    dispatch(updateLocalAssignments(items));
  },
  updateRosterTravelAssignment(item) {
    return dispatch(update(REDUCER_KEY.travelItinerary.key, {
      id: item.id.replace('t-', ''),
      notes: item.notes
    }))
      .then(() => {
        dispatch(updateTravelAssignments([item]));
      });
  },
  clearRosterLocalAssignments() {
    dispatch(clearLocalAssignments());
  },

  dispatchAddRosterEventDetails(insertTime, insertGroupId, users, selectedTemplate) {
    dispatch(addRosterEventDetails(insertTime, insertGroupId, users, selectedTemplate));
  },

  dispatchAddTimelineActivity(
    activity, group, time, activityCreateCount, activityHoursBetween, timelineMaxDays
  ) {
    dispatch(addTimelineActivity(
      activity, group, time, activityCreateCount, activityHoursBetween, timelineMaxDays
    ));
  },
  dispatchDeleteTimelineActivity() {
    dispatch(deleteTimelineActivity());
  },
  dispatchSelectTimelineActivities(itemIds) {
    dispatch(selectTimelineActivities(itemIds));
  },
  dispatchMoveTimelineActivities(changes, items, timelineMaxDays, groups) {
    dispatch(moveTimelineActivities(changes, items, timelineMaxDays, groups));
  },
  dispatchResizeTimelineActivities(changes, items, timelineMaxDays) {
    dispatch(resizeTimelineActivities(changes, items, timelineMaxDays));
  },
  dispatchSetTimelineGroups(userList, activityList, hideEmpty, hideUnallocatedRows) {
    dispatch(setTimelineGroups(userList, activityList, hideEmpty, hideUnallocatedRows));
  },
  swapTimelineAssignments(ids, items) {
    dispatch(swapTimelineAssignmentIds(ids, items));
  },
  dispatchToggleGroupPin(group) {
    dispatch(toggleGroupPin(group));
  },
  dispatchUpdatePinnedActivities(activities) {
    dispatch(updatePinnedActivities(activities));
  }
});

class RosterTimeline extends React.Component {
  constructor(props) {
    super(props);
    const { location } = this.props;

    const query = location.query || {};
    const startDate = query.s ? moment(parseFloat(query.s)) : moment()
      .add(-7, 'day');
    const endDate = moment(startDate)
      .add(settings.APP_CONFIG.defaultTimelineMaxDays, 'day');

    startDate.set({
      h: 0,
      m: 0,
      s: 0,
      ms: 0
    });
    endDate.set({
      h: 0,
      m: 0,
      s: 0,
      ms: 0
    });

    this.timeline9000RequiresRedraw = true;
    this.lastCursorPos = { anchorRowId: null, anchorTime: null };

    this.state = {
      selectedToolbarItem: null,
      selectedActivity: null,
      contextItem: {
        theme: null,
        object: {}
      },

      showToolbar: true,
      showTemplateMapper: false,
      showActivityForm: false,
      showActionForm: false,
      showReassignForm: false,
      showBulkActivityForm: false,
      showTravelInput: false,
      signOnPopover: null,

      userForm: {
        visible: false,
        userId: null
      },

      rosterItemsInFlight: false,

      // Intial timeline option settings. This is used to
      // retain the default timeline settings that is saved
      // against the roster and loaded on opening.
      defaultTimelineSettings: {
        ...defaultSettings
      },

      // Local timeline option settings. This retains the
      // current timeline option settings applied to the
      // roster.
      timelineSettings: {
        ...defaultSettings
      },

      timelineMode: TIMELINE_MODES.SELECT | TIMELINE_MODES.DRAG,

      timelineSorter: [
        {
          id: 'user',
          desc: false
        },
        {
          id: 'last_name',
          desc: false
        }
      ],
      customFilters: {
        user: {
          active: {},
          ids: {
            contains: false,
            searchValue: [],
            searchFields: ['id__in']
          },
          item: {
            contains: false,
            searchValue: '',
            searchFields: ['q']
          }
        },
        activity: {
          active: {},
          ids: {
            contains: false,
            searchValue: [],
            searchFields: ['id__in']
          },
          item: {
            contains: false,
            searchValue: '',
            searchFields: ['q']
          }
        }
      },

      visibleTimeStart: startDate,
      visibleTimeEnd: endDate,
      pasteMenu: {
        visible: false,
        x: 0,
        y: 0,
        anchorRowId: null,
        anchorTime: null
      }
    };

    // Toolbar
    this.handleToolbarItemChange = this.handleToolbarItemChange.bind(this);
    this.handleOptionsChange = this.handleOptionsChange.bind(this);

    this.handlePublishRoster = this.handlePublishRoster.bind(this);
    this.handleSaveRoster = this.handleSaveRoster.bind(this);
    this.handleRefreshRoster = this.handleRefreshRoster.bind(this);
    this.handleShowSettings = this.handleShowSettings.bind(this);
    this.handleShowToolbar = this.handleShowToolbar.bind(this);

    this.handleZoomIn = this.handleZoomIn.bind(this);
    this.handleZoomOut = this.handleZoomOut.bind(this);
    this.handlePanBack = this.handlePanBack.bind(this);
    this.handlePanForward = this.handlePanForward.bind(this);
    // Keyboard
    this.handleDeleteKeydown = this.handleDeleteKeydown.bind(this);
    this.handleCtrlKeyup = this.handleCtrlKeyup.bind(this);
    this.handleCtrlKeydown = this.handleCtrlKeydown.bind(this);
    // Mouse
    this.handleSingleMouseClick = this.handleSingleMouseClick.bind(this);
    this.handleItemClicks = this.handleItemClicks.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
    this.handleTimelineInteraction = this.handleTimelineInteraction.bind(this);
    // Groups
    this.handleGroupClick = this.handleGroupClick.bind(this);
    this.handleUserFormModalClose = this.handleUserFormModalClose.bind(this);
    this.handleGroupPin = this.handleGroupPin.bind(this);
    // Modals
    this.handleTemplateMapperModalOk = this.handleTemplateMapperModalOk.bind(this);
    this.handleTemplateMapperModalCancel = this.handleTemplateMapperModalCancel.bind(this);
    this.handleActivityFormModalOk = this.handleActivityFormModalOk.bind(this);
    this.handleActivityFormModalCancel = this.handleActivityFormModalCancel.bind(this);
    this.handleActionFormModalOk = this.handleActionFormModalOk.bind(this);
    this.handleActionFormModalCancel = this.handleActionFormModalCancel.bind(this);
    this.handleItemContextClick = this.handleItemContextClick.bind(this);
    this.handleReassignModalCancel = this.handleReassignModalCancel.bind(this);
    this.handleUserReassign = this.handleUserReassign.bind(this);
    this.handleSignOnApply = this.handleSignOnApply.bind(this);
    this.handleSignOnClose = this.handleSignOnClose.bind(this);
    // Other
    this.filterActivities = this.filterActivities.bind(this);
    this.renderGroupTitle = this.renderGroupTitle.bind(this);
    this.isReadOnly = this.isReadOnly.bind(this);

    this.handleCustomFilters = this.handleCustomFilters.bind(this);
    this.getSidebar = this.getSidebar.bind(this);
    this.handlePinItem = this.handlePinItem.bind(this);

    this.renderToolbar = this.renderToolbar.bind(this);
    this.renderItem = this.renderItem.bind(this);
    this.renderGroup = this.renderGroup.bind(this);

    this.requireRedraw = this.requireRedraw.bind(this);
    this.handleBulkAssignmentModalCancel = this.handleBulkAssignmentModalCancel.bind(this);

    this.handleNotifications = this.handleNotifications.bind(this);
    this.handleNotificationBulkDelete = this.handleNotificationBulkDelete.bind(this);

    this.unselectAssignments = this.unselectAssignments.bind(this);

    this.handleNotificationDelete = this.handleNotificationDelete.bind(this);
    this.handleBulkNotifications = this.handleBulkNotifications.bind(this);

    this.handlePostNotificationDelete = this.handlePostNotificationDelete.bind(this);
    this.handleBulkActivitySave = this.handleBulkActivitySave.bind(this);

    this.getValidationErrors = this.getValidationErrors.bind(this);

    // Memoized render-time derivations. memoize-one caches the last result keyed on
    // argument identity, so selection/loading-flag/unrelated state changes no longer
    // rebuild the whole item graph — only genuine changes to the source arrays/options
    // recompute. This also gives Timeline stable item/layer references (see requireRedraw
    // and the Timeline relayout guard).
    this.deriveValidationErrors = memoizeOne(computeValidationErrors);
    this.deriveRosterItems = memoizeOne(rosterPivot.getRosterItems);
    // Extra call args (filter options + activity filter) participate in memoize-one's
    // argument comparison even though the body only needs `items`; this.filterActivities
    // reads those same values from state, so the memo recomputes exactly when they change.
    this.deriveTimelineItems = memoizeOne(items => items.filter(this.filterActivities).map(mapAssignments));
    this.deriveAvailabilityLayers = memoizeOne(getAvailabilityLayers);
    this.deriveSelectedItems = memoizeOne((items, selectedAssignments) => items
      .filter(item => selectedAssignments.includes(item.id))
      .map(mapAssignments));

    // Items for a multi-selection, so each bar's menu can label "Set OT availability for {n}…".
    this.memoSelectedItems = memoizeOne((selected, userGroups, localItems, serverItems) => {
      if (selected.length < 2) {
        return [];
      }
      const ids = new Set(selected);
      return rosterPivot.getUserGroupItems(userGroups, localItems, serverItems, [])
        .filter(i => ids.has(i.id));
    });

    this.handleTravelInputOk = this.handleTravelInputOk.bind(this);
    this.handleTravelInputCancel = this.handleTravelInputCancel.bind(this);

    this.handleTravelModalOk = this.handleTravelModalOk.bind(this);
  }

  async componentDidMount() {
    initRosterNetworkObserver();
    const {
      match,
      loadActionContext,
      clearRosterLocalAssignments,
      dispatchClearClipboard
    } = this.props;
    const { params, router } = match;
    const rosterVersionId = params.id;

    this.loadRoster(rosterVersionId);

    loadActionContext();
    clearRosterLocalAssignments();

    if (isNavigationBlocked()) {
      history.block((nextLocation) => {
        if (isNavigationBlocked()) {
          promptNavigation(() => {
          },
          () => {
            // Discard changes
            blockNavigation(false);
            router.push(nextLocation);
          });
          return false;
        }
        return true;
      });
    }

    document.addEventListener('click', this.handleDocumentClickForPasteMenu);
    dispatchClearClipboard();
  }

  componentWillUnmount() {
    blockNavigation(false);
    document.removeEventListener('click', this.handleDocumentClickForPasteMenu);
  }

  componentDidUpdate(prevProps, prevState) {
    const { signOnPopover, visibleTimeStart, visibleTimeEnd } = this.state;
    if (signOnPopover && (prevState.visibleTimeStart !== visibleTimeStart || prevState.visibleTimeEnd !== visibleTimeEnd)) {
      this.handleSignOnClose();
    }
    // R19b: if the targets were deleted while the popover was open, close it.
    if (signOnPopover && this.getSignOnTargets().length === 0) {
      this.handleSignOnClose();
    }

    // Mark 6: React has finished committing this update to the DOM.
    perfMark('react-commit');

    // Mark 7: when the aggregate loading flag flips false, the timeline has its
    // data and has committed; schedule two nested rAFs to catch the paint after.
    const {
      rosterVersionInFlight, usersInFlight, activityInFlight
    } = this.props;
    const { rosterItemsInFlight } = this.state;
    const wasLoading = prevProps.rosterVersionInFlight
      || prevState.rosterItemsInFlight
      || prevProps.usersInFlight
      || prevProps.activityInFlight;
    const isLoading = rosterVersionInFlight
      || rosterItemsInFlight
      || usersInFlight
      || activityInFlight;
    if (wasLoading && !isLoading) {
      perfMark('loading-false');
      markNextTwoFrames('afterLoadingFalse');
    }
  }

  getTimelineContainerRef = (input) => {
    this.timelineContainer = input;
  };

  getTimelineRef = (input) => {
    this.timeline = input;
  };

  getTemplateMapperRef = (input) => {
    this.templateMapper = input != null ? input.wrappedInstance : null;
  };

  getUserReassignRef = (form) => {
    this.userReassignRef = form;
  };

  getActivityFormRef = (form) => {
    this.activityForm = form;
  };

  getTravelFormRef = (form) => {
    this.travelForm = form;
  };

  getActionFormRef = (form) => {
    this.actionForm = form && form.wrappedInstance;
  };

  getSidebar(element) {
    this.sidebar = element || this.sidebar;
  }

  getValidationErrors() {
    const { validationTasks } = this.props;
    return this.deriveValidationErrors(validationTasks);
  }

  isReadOnly() {
    const { permissions } = this.props;
    const { timelineSettings } = this.state;

    return !permissions.CanAdminRosters()
      .hasPermissions() || timelineSettings.options.rosterPivoted;
  }

  async loadDefaultSettingsAndTimeline(rosterVersion) {
    const defaultRosterSettings = rosterVersion.default_settings || {};
    const { customFilters: custom, visibleTimeStart, visibleTimeEnd } = this.state;
    // initialise customFilters from server
    let customFilters = {
      ...custom
    };
    // update filtered users based on tags changing
    if (!_.isEmpty(defaultRosterSettings)) {
      customFilters = {
        ...customFilters,
        user: {
          ...customFilters.user,
          item: {
            ...customFilters.activity.item,
            searchValue: defaultRosterSettings.filters.user.search
          },
          active: {
            ...(defaultRosterSettings.filters.user.active) && {
              contains: false,
              searchValue: true,
              searchFields: [
                'is_active'
              ]
            }
          }
        },
        activity: {
          ...customFilters.activity,
          item: {
            ...customFilters.activity.item,
            searchValue: defaultRosterSettings.filters.activity.search
          },
          active: {
            ...(defaultRosterSettings.filters.activity.active) && {
              contains: false,
              searchValue: true,
              searchFields: [
                'is_active',
                'active'
              ]
            }
          }
        }
      };
      // if there were existing tags then fetch new set of ids
      if (defaultRosterSettings.filters.user.tags.length) {
        await getTaggedItems(defaultRosterSettings.filters.user.tags, defaultRosterSettings.filters.user.allTags, visibleTimeStart, visibleTimeEnd)
          .then((response) => {
            const tagged = response.length > 0 ? response.map(item => item.object_id) : [-1];
            customFilters = {
              ...customFilters,
              user: {
                ...customFilters.user,
                ids: {
                  ...customFilters.user.ids,
                  searchValue: tagged.length ? tagged : []
                }
              }
            };
          });
      }
      // update filtered actvities based on tags changing
      if (defaultRosterSettings.filters.activity.tags.length) {
        // if there were existing tags then fetch new set of ids
        await getTaggedItems(defaultRosterSettings.filters.activity.tags, defaultRosterSettings.filters.user.allTags)
          .then((response) => {
            const tagged = response.length > 0 ? response.map(item => item.object_id) : [-1];
            customFilters = {
              ...customFilters,
              activity: {
                ...customFilters.activity,
                ids: {
                  ...customFilters.activity.ids,
                  searchValue: tagged.length ? tagged : []
                }
              }
            };
          });
      }
    }
    // update state settings
    if (!_.isEmpty(defaultSettings)) {
      this.setState(prevState => ({
        defaultTimelineSettings: {
          ...prevState.defaultTimelineSettings,
          ...defaultRosterSettings
        },
        timelineSettings: {
          ...prevState.timelineSettings,
          ...defaultRosterSettings
        },
        customFilters: {
          ...prevState.customFilters,
          ...customFilters
        }
      }), () => {
        this.loadRosterTimeline(rosterVersion.id);
      });
    }
    else {
      this.loadRosterTimeline(rosterVersion.id);
    }
  }

  loadRoster(rosterVersionId = -1) {
    blockNavigation(false);
    this.setState({ rosterItemsInFlight: true }, () => {
      const { loadRosterVersion } = this.props;

      if (rosterVersionId > 0) {
        // Load the Roster Version Details
        loadRosterVersion(rosterVersionId)
          .then((response) => {
            const rosterVersion = response;
            this.loadDefaultSettingsAndTimeline(rosterVersion);
          },
          (error) => {
            this.setState({ rosterItemsInFlight: false }, () => {
              if (error.fieldErrors.indexOf('detail - Not found.') >= 0) {
                history.push('/roster/list/');
              }
            });
          });
      }
      else {
        history.push('/roster/list/');
        this.setState({ rosterItemsInFlight: false });
      }
    });
  }

  loadRosterTimeline(rosterVersionId, serverFetch = true, clearLocal = false, forceLists = false) {
    // Each load gets a generation id; if a newer load starts before this one resolves, the
    // stale results are dropped (prevents an older filter's response overwriting a newer one).
    const generation = (this.loadGeneration || 0) + 1;
    this.loadGeneration = generation;
    const isCurrent = () => this.loadGeneration === generation;

    this.setState({ rosterItemsInFlight: true }, () => {
      const { dispatchSetTimelineGroups, loadUserList, loadActivityList } = this.props;
      const { timelineSorter, customFilters, timelineSettings } = this.state;
      const { hideEmptyRows, hideUnallocatedRows } = timelineSettings.options;

      const userFilterBy = getCustomFilters(customFilters.user);
      const activityFilterBy = getCustomFilters(customFilters.activity);

      // The user and activity master lists depend ONLY on their own filters and sort:
      // getCustomFilters reads no dates, no pins and no timeline options. So pinning a row,
      // panning, zooming, saving or flipping a display option all re-requested byte-identical
      // 1000-row lists. Cache the last response against the request signature and reuse it
      // unless the signature changed (a real filter/sort change) or an explicit refresh asks
      // for fresh data. Assignments are always re-requested when serverFetch is set - only
      // these two lookup lists are cached.
      const cache = this.listCache || {};
      const userKey = JSON.stringify([userFilterBy, timelineSorter]);
      const activityKey = JSON.stringify(activityFilterBy);
      const activityRequest = (!forceLists && cache.activityKey === activityKey)
        ? Promise.resolve(cache.activityList)
        : loadActivityList(activityFilterBy).then(response => response.results);
      const userRequest = (!forceLists && cache.userKey === userKey)
        ? Promise.resolve(cache.userList)
        : loadUserList(userFilterBy, timelineSorter).then(response => response.results);

      // Load the activity and user groups for the timeline then load the user assignments.
      Promise.all([activityRequest, userRequest])
        .then((values) => {
          if (!isCurrent()) {
            return; // a newer load has superseded this one
          }
          const activityList = values[0];
          const userList = values[1];
          this.listCache = {
            userKey, activityKey, userList, activityList
          };
          // Pass the freshly loaded user ids straight to the assignment loader rather than
          // round-tripping through the (async-updated) user_groups prop. (userList may be
          // undefined when the endpoint returns a bare list; guard before mapping.)
          // Pinned users stay visible as rows even when the current filter excludes them
          // (the reducer re-adds them to user_groups), so we must still fetch their
          // assignments — otherwise a pinned-but-filtered-out user renders an empty row.
          // Union the filtered user ids with the currently-pinned user ids. Pins persist in
          // user_groups across filter changes, so reading them from props here is safe.
          const { user_groups: currentUserGroups } = this.props;
          const pinnedUserIds = (currentUserGroups || [])
            .filter(group => group.pinNumber > 1 && group.uid !== -1)
            .map(group => group.uid);
          const userIds = [...new Set([
            ...(userList || [])
              .filter(user => user.id !== -1 && user.id !== null)
              .map(user => user.id),
            ...pinnedUserIds
          ])];
          dispatchSetTimelineGroups(userList, activityList, false);
          if (serverFetch) {
            this.loadRosterUserAssignments(
              rosterVersionId,
              clearLocal,
              userIds,
              isCurrent,
              () => {
                if (!isCurrent()) {
                  return;
                }
                dispatchSetTimelineGroups(
                  userList,
                  activityList,
                  hideEmptyRows,
                  hideUnallocatedRows
                );
                this.setState({ rosterItemsInFlight: false });
              }
            );
          }
          else {
            dispatchSetTimelineGroups(userList, activityList, hideEmptyRows, hideUnallocatedRows);
            this.setState({ rosterItemsInFlight: false });
          }
        });
    });
  }

  loadRosterUserAssignments(rosterVersionId, clearLocal = true, userIdsOverride = null, isCurrent = null, onResultsApplied = null) {
    const { loadRosterServerAssignments, clearRosterLocalAssignments, user_groups } = this.props;
    // Show the "Loading..." indicator while assignments refresh (e.g. panning with "hide
    // empty rows" off, which loads here directly rather than via loadRosterTimeline). The
    // in-flight flag is cleared in the result/rejection handlers below.
    this.setState({ rosterItemsInFlight: true });
    const { visibleTimeStart, visibleTimeEnd, timelineSettings } = this.state;
    const {
 displayTravelLayer, displayLeaveLayer, displayAvailabilityLayer, hideUnallocatedRows
} = timelineSettings.options;
    const userIds = userIdsOverride !== null
      ? userIdsOverride
      : user_groups.filter(user => user.uid !== -1)
        .map(g => g.uid);
    // Refreshes from server, discarding any local changes
    return loadRosterServerAssignments(
      rosterVersionId,
      userIds,
      visibleTimeStart,
      visibleTimeEnd,
      displayTravelLayer,
      displayLeaveLayer,
      displayAvailabilityLayer,
      () => {
        if (isCurrent && !isCurrent()) {
          return;
        }
        if (clearLocal) {
          clearRosterLocalAssignments();
          blockNavigation(false);
        }
        this.setState({ rosterItemsInFlight: false });
        if (onResultsApplied) {
          onResultsApplied();
        }
      },
      isCurrent,
      // Skip fetching unallocated assignments when their row is hidden — the reducer
      // discards them in that state anyway, so fetching them is wasted payload.
      !hideUnallocatedRows
    )
      .then(
        () => {},
        () => {
          if (isCurrent && !isCurrent()) {
            return;
          }
          this.setState({ rosterItemsInFlight: false });
        }
      );
  }

  // The old `refresh` flag only gated a duplicate assignments request; the reload below always
  // ran regardless, so both call sites passed true and it is gone.
  saveRosterVersion(onSave = noOp) {
    const { timelineSettings, visibleTimeStart, visibleTimeEnd } = this.state;
    if (rosterPivot.getGroup(timelineSettings.options) !== rosterPivot.PIVOT_TYPES.USER) {
      message.warning('Roster can\'t be saved while pivoted');
      return;
    }
    const {
      loadValidationTasks, dispatchSelectTimelineActivities, saveRosterLocalAssignments, checkCompetencies, match, local_items, deleted_items, travel_items
    } = this.props;
    const { params } = match;
    const rosterVersionId = params.id;

    this.setState({ rosterItemsInFlight: true });
    const itemsToAdd = local_items.map(i => ({
      user_id: i.user_id,
      activity_id: i.activity.id,
      // Coerce to Moment at the point of use: local items may carry epoch-ms times
      // (ADD_ACTIVITY_TO_TIMELINE) and we no longer rely on a render-time mutation in
      // getUserGroupItems to convert them.
      check_datetime: moment(i.end_time).format('YYYY-MM-DD HH:mm:ss')
    }));
    const saveFunc = () => {
      saveRosterLocalAssignments(local_items, deleted_items, travel_items, rosterVersionId, visibleTimeStart, visibleTimeEnd)
        .then(() => {
          this.setState({ rosterItemsInFlight: false }, onSave);
          blockNavigation(false);
          message.success('Save Successful.');
          dispatchSelectTimelineActivities([]);
          // loadRosterTimeline re-requests the assignments itself (serverFetch), so the
          // separate loadRosterUserAssignments call that used to sit above it just issued a
          // second, identical assignments request racing this one.
          this.loadRosterTimeline(rosterVersionId, true, true);
          loadValidationTasks(rosterVersionId, visibleTimeStart, visibleTimeEnd);
        },
        (e) => {
          this.setState({ rosterItemsInFlight: false });
          message.error(`Save Failed: ${e.allErrors}`);
          dispatchSelectTimelineActivities([]);
        });
    };

    // No new items
    if (!itemsToAdd.length) {
      saveFunc();
    }
    else {
      // check competencies for new Roster Assignments
      checkCompetencies(itemsToAdd)
        .then((r) => {
          if (r.length > 0) {
            const self = this;
            const itemsToDisplay = _.uniqWith(r, _.isEqual);
            Modal.confirm({
              title: 'Competency Warnings',
              content: (
                <div>
                  <p>
                    The following users do not have the required competencies for their assigned activities:
                  </p>
                  <div style={{
                    overflowY: 'auto',
                    maxHeight: '100px'
                  }}
                  >
                    {itemsToDisplay.map(i => (
                      <div key={`${i.id}-${i.user_id}-${i.activity_id}`}>
                        <span>
                          <b>{i.user_name}</b>
                          {' '}
                          requires
                          <b>{i.name}</b>
                          {' '}
                          for
                          <b>{i.activity_name}</b>
                        </span>
                        <br />
                      </div>
                    ))}
                  </div>
                </div>
              ),
              okText: 'Ignore competencies and save roster',
              cancelText: 'Cancel',
              onOk() {
                saveFunc();
              },
              onCancel() {
                self.setState({ rosterItemsInFlight: false });
                message.warning('Save aborted');
              },
              width: '700px'
            });
          }
          else {
            saveFunc();
          }
        });
    }
  }

  publishRoster() {
    blockNavigation(false);
    const { match, local_items, deleted_items } = this.props;

    const { params } = match;

    const rosterVersionId = params.id;
    const shouldSave = local_items.length + deleted_items.length > 0;
    const urlParams = (this.timeline ? `?s=${this.timeline.state.visibleTimeStart}&e=${this.timeline.state.visibleTimeEnd}` : '');
    const publishUrl = `/roster/publish/${rosterVersionId}${urlParams}`;

    if (shouldSave) {
      this.saveRosterVersion(() => {
        history.push(publishUrl);
      });
    }
    else {
      history.push(publishUrl);
    }
  }

  filterActivities(data) {
    const { timelineSettings } = this.state;
    const { timeFilterStart, timeFilterEnd } = timelineSettings.options;
    const timeFilterStartMoment = timeFilterStart instanceof moment ? timeFilterStart : null; // if not a valid format ignore
    const timeFilterEndMoment = timeFilterEnd instanceof moment ? timeFilterEnd : null; // if not a valid format ignore
    if (isTravelItem(data) || isLeaveItem(data)) {
      return true;
    }
    const { customFilters } = this.state;

    const { item, ids, active = {} } = customFilters.activity;

    const activityItem = item.searchValue || '';
    const activityIds = ids.searchValue || [];

    let matched = true;

    for (const activityFilter of activityItem) {
      matched = true;
      matched = matched && data.title.toLowerCase() === activityFilter.label.toLowerCase();

      // if the activity match one of multiple activity filter then break
      if (matched) {
        break;
      }
    }

    if (activityIds.length) {
      matched = matched && activityIds.includes(data.activity.id);
    }

    if (active.searchValue) {
      matched = matched && data.activity.active;
    }

    if (timeFilterStartMoment) {
      // If defined should remove assignments that start < given time
      const filterHrs = timeFilterStartMoment.hour();
      const filterMins = timeFilterStartMoment.minute();
      const activityHrs = data.start_time.hour();
      const activityMins = data.end_time.minute();
      if (activityHrs < filterHrs) {
        return false;
      }
      if (activityHrs === filterHrs && activityMins < filterMins) {
        return false;
      }
    }

    if (timeFilterEndMoment) {
      // If defined should remove assignments that end > given time
      const filterHrs = timeFilterEndMoment.hour();
      const filterMins = timeFilterEndMoment.minute();
      const activityHrs = data.start_time.hour();
      const activityMins = data.end_time.minute();

      if (activityHrs > filterHrs) {
        return false;
      }
      if (activityHrs === filterHrs && activityMins > filterMins) {
        return false;
      }
    }

    return matched;
  }

  addTemplate(template, users, userId, time) {
    // Row ids are positions, so row 0 is only unallocated when that row is shown.
    const group = _.find(users, g => g.id === userId);
    if (group && group.uid === -1) {
      message.error('Templates can\'t be applied to the unallocated user.');
    }
    else {
      const { dispatchAddRosterEventDetails } = this.props;
      dispatchAddRosterEventDetails(time, userId, users, template);
      this.setState({ showTemplateMapper: true });
    }
  }

  handleZoomIn() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities, match } = this.props;

    const { params } = match;

    const rosterVersionId = params.id;
    const diffMinutes = Math.max(visibleTimeEnd.diff(visibleTimeStart, 'minutes') / 2, MIN_ZOOM_DAYS * 24 * 60);

    dispatchSelectTimelineActivities();
    this.setState(prevState => ({
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(diffMinutes, 'minutes')
    }), () => {
      this.loadRosterTimeline(rosterVersionId);
    });
  }

  handleBulkNotifications(response, data) {
    const {
      swapTimelineAssignments, user_groups, local_items, server_items, dispatchSelectTimelineActivities
    } = this.props;
    const { id_map = {} } = response; // response sends mapped ids
    // get all items
    const items = rosterPivot.getUserGroupItems(user_groups, local_items, server_items, []);
    const { context_object_id } = data; // the context_objects
    const updatedItems = items.filter(item => context_object_id.includes(item.id))
      .map((item) => {
        // editing a notification
        item.notifications.push({
          ...data,
          status: 'P',
          success_date: null,
          id: Date.now()
        });
        return getItem(id_map, item);
      });

    // mapped ids and asssignment ids and make unique
    const ids = [...new Set(Object.keys(id_map)
      .map(i => Number(i))
      .concat(updatedItems.map(item => item.id)))];
    swapTimelineAssignments(ids, updatedItems);
    dispatchSelectTimelineActivities(Object.values(id_map)
      .map(i => Number(i))); // re-select existing assignments with new ids
  }

  handleBulkActivitySave(id, item) {
    const {
      selectedAssignments, activities, updateRosterLocalAssignments, user_groups, local_items, server_items
    } = this.props;
    const { activity: selectedActivity = {}, notes, admin_notes } = item;
    const activity = activities.find(a => a.id === selectedActivity.key);
    // get all items
    const items = rosterPivot.getUserGroupItems(user_groups, local_items, server_items, []);
    const updatedItems = items.filter(i => selectedAssignments.includes(i.id))
      .map(i => ({
        ...i,
        ...(activity) && { activity },
        // Moving to an activity without sign-on windows clears the value now, so the bar
        // and tooltip are right before Save (the server would clear it anyway).
        ...(activity && !isOta(activity)) && { available_sign_on: null },
        ...(notes) && { notes },
        ...(admin_notes) && { admin_notes }
      }));
    blockNavigation(true);
    updateRosterLocalAssignments(updatedItems);
  }

  handleNotifications(response, data) {
    const {
      user_groups, local_items, server_items, swapTimelineAssignments
    } = this.props;
    const { selectedActivity } = this.state;

    const {
      id_map = {}, id, status, is_before, delta, type, parent
    } = response; // response sends mapped ids
    // get all items
    const items = rosterPivot.getUserGroupItems(user_groups, local_items, server_items, []);

    const { context_object_id } = data; // the context_objects
    const updatedItems = items.filter(item => context_object_id.includes(item.id))
      .map((item) => {
        // get notifications
        const { notifications } = item;
        // editing a notification
        if (id) {
          notifications.forEach((notification, i) => {
            if (notification.id === id) {
              notifications[i] = {
                id,
                status,
                is_before,
                delta,
                type,
                parent
              };
            }
          });
        }
        else { // we are adding
          item.notifications.push({
            ...data,
            status: 'P',
            success_date: null,
            id: Date.now()
          });
        }
        return getItem(id_map, item);
      });
    // mapped ids and asssignment ids and make unique
    const ids = [...new Set(Object.keys(id_map)
      .map(i => Number(i))
      .concat(updatedItems.map(item => item.id)))];
    swapTimelineAssignments(ids, updatedItems);

    this.setState({
      selectedActivity: {
        ...selectedActivity,
        id: Object.values(id_map)
          .map(i => Number(i))[0] || selectedActivity.id
      }
    });
  }

  handleNotificationDelete(response) {
    const { id, data } = response;
    const { id_map = {} } = data;
    const {
      user_groups, local_items, server_items, swapTimelineAssignments
    } = this.props;
    const { selectedActivity } = this.state;
    // get all items
    const items = rosterPivot.getUserGroupItems(user_groups, local_items, server_items, []);
    const updatedItems = items.filter(item => selectedActivity.id === item.id)
      .map((item) => {
        // editing a notification
        const { notifications } = this.props; // notifications from store to access server
        return getItem(id_map, {
          ...item,
          notifications: notifications.filter(notification => notification.id !== id) // use notification store and remove
        });
      });

    // mapped ids and asssignment ids and make unique
    const ids = [...new Set(Object.keys(id_map)
      .map(i => Number(i))
      .concat(updatedItems.map(item => item.id)))];
    // swap out local/server items
    swapTimelineAssignments(ids, updatedItems);
  }

  handlePostNotificationDelete(response, callback) {
    const { selectedActivity } = this.state;
    const { data } = response;
    const { id_map = {} } = data;
    // update selected assignments
    this.setState({
      selectedActivity: {
        ...selectedActivity,
        id: Object.values(id_map)
          .map(i => Number(i))[0] || selectedActivity.id
      }
    }, () => {
      callback(); // reload the list and process the swapping. Triggers onDelete
    });
  }

  handleNotificationBulkDelete(response) {
    const { data } = response;
    const { id_map = {} } = data;
    const {
      selectedAssignments, dispatchSelectTimelineActivities, swapTimelineAssignments, user_groups, local_items, server_items
    } = this.props;
    const items = rosterPivot.getUserGroupItems(user_groups, local_items, server_items, []);
    const updatedItems = items.filter(item => selectedAssignments.includes(item.id))
      .map(item => getItem(id_map, {
        ...item,
        notifications: [] // empty the notifications
      }));
    // mapped ids and asssignment ids and make unique
    const ids = [...new Set(Object.keys(id_map)
      .map(i => Number(i))
      .concat(updatedItems.map(item => item.id)))];
    // swap out local/server items
    swapTimelineAssignments(ids, updatedItems);
    dispatchSelectTimelineActivities(ids); // re-select existing assignments with new ids
  }

  handleZoomOut() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { dispatchSelectTimelineActivities, match } = this.props;

    const { params } = match;

    const rosterVersionId = params.id;
    const diffMinutes = Math.min(2 * visibleTimeEnd.diff(visibleTimeStart, 'minutes'), MAX_ZOOM_DAYS * 24 * 60);

    dispatchSelectTimelineActivities();
    this.setState(prevState => ({
      visibleTimeEnd: moment(prevState.visibleTimeStart)
        .add(diffMinutes, 'minutes')
    }), () => {
      this.loadRosterTimeline(rosterVersionId);
    });
  }

  handlePanForward() {
    const { visibleTimeStart, visibleTimeEnd, timelineSettings } = this.state;
    const { loadValidationTasks, dispatchSelectTimelineActivities, match } = this.props;

    const { params } = match;

    const rosterVersionId = params.id;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days') / 2;

    this.setState({
      visibleTimeStart: moment(visibleTimeStart)
        .add(diffDays, 'days'),
      visibleTimeEnd: moment(visibleTimeEnd)
        .add(diffDays, 'days')
    }, () => {
      const { hideEmptyRows } = timelineSettings.options;
      if (!hideEmptyRows) {
        dispatchSelectTimelineActivities();
        this.loadRosterUserAssignments(rosterVersionId, false);
      }
      else {
        this.loadRosterTimeline(rosterVersionId, true);
      }
      loadValidationTasks(rosterVersionId, visibleTimeStart, visibleTimeEnd);
    });
  }

  handlePanBack() {
    const { visibleTimeStart, visibleTimeEnd, timelineSettings } = this.state;
    const { loadValidationTasks, dispatchSelectTimelineActivities, match } = this.props;
    const { params } = match;

    const rosterVersionId = params.id;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days') / 2;

    this.setState({
      visibleTimeStart: moment(visibleTimeStart)
        .add(-diffDays, 'days'),
      visibleTimeEnd: moment(visibleTimeEnd)
        .add(-diffDays, 'days')
    }, () => {
      const { hideEmptyRows } = timelineSettings.options;
      if (!hideEmptyRows) {
        dispatchSelectTimelineActivities();
        this.loadRosterUserAssignments(rosterVersionId, false);
      }
      else {
        this.loadRosterTimeline(rosterVersionId, true);
      }
      loadValidationTasks(rosterVersionId, visibleTimeStart, visibleTimeEnd);
    });
  }

  handleCtrlKeydown() {
    const { timelineMode } = this.state;
    const resizeEnabled = isBitSet(Timeline.TIMELINE_MODES.RESIZE, timelineMode);

    if (!resizeEnabled) {
      let newTimelineMode = timelineMode;
      newTimelineMode &= ~TIMELINE_MODES.DRAG; // Turn off drag
      newTimelineMode |= TIMELINE_MODES.RESIZE; // Turn on resie
      this.setState({ timelineMode: newTimelineMode });
    }
  }

  handleCtrlKeyup() {
    const { timelineMode } = this.state;
    let newTimelineMode = timelineMode;
    newTimelineMode |= TIMELINE_MODES.DRAG; // Turn on drag
    newTimelineMode &= ~TIMELINE_MODES.RESIZE; // Turn off resie
    this.setState({ timelineMode: newTimelineMode });
  }

  handleDeleteKeydown() {
    if (this.isReadOnly()) {
      return;
    }

    const { dispatchDeleteTimelineActivity } = this.props;
    const { showTemplateMapper, signOnPopover } = this.state;

    // R19a: Delete/Backspace inside the popover must not delete the selected assignments.
    if (signOnPopover) {
      return;
    }

    if (!showTemplateMapper) {
      blockNavigation(true);
      dispatchDeleteTimelineActivity();
    }
  }

  handlePublishRoster() {
    this.publishRoster();
  }

  handleSaveRoster() {
    this.saveRosterVersion();
  }

  handleRefreshRoster() {
    const { match, loadValidationTasks } = this.props;
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { params } = match;
    const rosterVersionId = params.id;
    // Explicit refresh: bypass the user/activity list cache so a user or activity edited
    // elsewhere shows up.
    this.loadRosterTimeline(rosterVersionId, true, true, true);
    loadValidationTasks(rosterVersionId, visibleTimeStart, visibleTimeEnd);
  }

  /*
   ** @description Update the settings states and call the save event
   ** @triggers onSaveSettings func
   */
  handleShowSettings() {
    const { defaultTimelineSettings } = this.state;
    const { userFilter, activityFilter, timelineOptions } = this.sidebar;
    // get user filter values from form
    const user = userFilter ? userFilter.state.filters : {
      ...defaultSettings.filters.user
    };
    // get activity filter values from form
    const activity = activityFilter ? activityFilter.state.filters : {
      ...defaultSettings.filters.activity
    };
    // get the options values from form
    const options = timelineOptions ? timelineOptions.state.options : {
      ...defaultTimelineSettings.options
    };
    // update the timelineSettings with new values
    this.setState(prevState => ({
      timelineSettings: {
        ...prevState.timelineSettings,
        filters: {
          user: {
            ...user
          },
          activity: {
            ...activity
          }
        },
        options: {
          ...options
        }
      }
    }), () => {
      const { defaultTimelineSettings: defaultRosterSettings, timelineSettings, customFilters } = this.state;
      const { onSaveSettings } = this.props;
      // save the settings and current filter params
      // Pass the settings and callback for success
      onSaveSettings({
        defaultSettings: {
          ...defaultRosterSettings
        },
        currentSettings: {
          ...timelineSettings,
          customFilters
        }
      },
      () => {
        this.setState({
          defaultTimelineSettings: {
            ...timelineSettings
          }
        });
      });
    });
  }

  handleShowToolbar() {
    this.setState(prevState => ({
      showToolbar: !prevState.showToolbar
    }));
  }

  handleToolbarItemChange(item) {
    this.setState({ selectedToolbarItem: item });
  }

  handleCustomFilters(customFilters) {
    this.setState(prevState => ({
      rosterItemsInFlight: true,
      customFilters: {
        ...prevState.customFilters,
        ...customFilters
      }
    }), () => {
      const { match } = this.props;
      const { params } = match;
      const rosterId = params.id;
      // loadRosterTimeline keys its list cache on the filters themselves, so a user-only
      // filter change reuses the cached activity list without needing to be told.
      this.loadRosterTimeline(rosterId);
    });
  }

  handleOptionsChange(options) {
    const { timelineSettings } = this.state;
    const previousOptions = timelineSettings.options;

    this.setState(prevState => ({
      timelineSettings: {
        ...prevState.timelineSettings,
        options
      }
    }), () => {
      const { dispatchSelectTimelineActivities, match } = this.props;

      const { params } = match;

      // When the Roster timeline is pivoted, clear
      // any selected activity items.
      if (options.rosterPivoted) {
        dispatchSelectTimelineActivities([]);
      }
      // This runs on EVERY option change, including purely presentational ones
      // (assignmentTitleFormat, timelineSnapMinutes, activityCreateCount,
      // activityHoursBetween, timelineMaxDays, rosterPivot, timeFilter), which used to
      // re-request every assignment in the window. Only two things can need new data:
      //
      //  - a layer toggle. The store holds travel/leave/availability items unfiltered and the
      //    renderer draws whatever is there, so turning a layer OFF has to refetch to drop the
      //    items, just as turning it ON has to refetch to get them.
      //  - unhiding the unallocated row: its assignments are deliberately not requested while
      //    the row is hidden (see loadRosterUserAssignments), so they have to be fetched now.
      //
      // Hiding the unallocated row and either direction of hideEmptyRows only change which
      // rows the reducer keeps, and items with no visible group are dropped by the selector -
      // so they need the groups recomputing (serverFetch false, which the list cache serves
      // with no request at all) but no network call. Anything else needs nothing.
      const layerChanged = options.displayTravelLayer !== previousOptions.displayTravelLayer
        || options.displayLeaveLayer !== previousOptions.displayLeaveLayer
        || options.displayAvailabilityLayer !== previousOptions.displayAvailabilityLayer;
      const unallocatedShown = !options.hideUnallocatedRows && previousOptions.hideUnallocatedRows;
      const requireRegroup = options.hideEmptyRows !== previousOptions.hideEmptyRows
        || options.hideUnallocatedRows !== previousOptions.hideUnallocatedRows;

      if (layerChanged || unallocatedShown) {
        this.loadRosterTimeline(params.id, true);
      }
      else if (requireRegroup) {
        this.loadRosterTimeline(params.id, false);
      }
    });
  }

  handleTemplateMapperModalOk() {
    // ------ Validate and retrieve the form data ------
    this.templateMapper.applyTemplate()
      .then((applied) => {
        if (applied) {
          this.setState({ showTemplateMapper: false });
        }
      });
  }

  handleTemplateMapperModalCancel() {
    this.setState({ showTemplateMapper: false });
  }

  handleTravelInputOk() {
    this.setState({ showTravelInput: true });
  }

  handleTravelInputCancel() {
    this.setState({ showTravelInput: false });
  }

  handleUserReassign() {
    const {
      user_groups, local_items, server_items, selectedAssignments, updateRosterLocalAssignments
    } = this.props;
    const { selectedActivity } = this.state;
    const newUser = this.userReassignRef.getWrappedInstance().selectedUser;
    const newGroup = user_groups.find(group => group.uid === newUser.id);
    const newGroupId = newGroup ? newGroup.id : 0;
    const items = rosterPivot.getUserGroupItems(user_groups, local_items, server_items, []);
    const selected = [...selectedAssignments, selectedActivity && selectedActivity.id];

    const updatedItems = items.filter(i => selected.includes(i.id))
      .map(i => ({
        ...i,
        user: newUser,
        user_id: newUser.id,
        group: newGroupId
      }));
    blockNavigation(true);
    updateRosterLocalAssignments(updatedItems);
    this.setState({
      showReassignForm: false,
      selectedActivity: null
    });
  }

  getSignOnTargets() {
    const { signOnPopover } = this.state;
    const { user_groups, local_items, server_items } = this.props;
    const ids = new Set(signOnPopover.ids);
    return rosterPivot.getUserGroupItems(user_groups, local_items, server_items, [])
      .filter(i => ids.has(i.id));
  }

  handleSignOnApply(localValue) {
    const { updateRosterLocalAssignments } = this.props;
    const targets = this.getSignOnTargets();
    blockNavigation(true);
    updateRosterLocalAssignments(applySignOn(targets, localValue));
    message.success(signOnToast(targets, localValue));
    this.handleSignOnClose();
  }

  handleSignOnClose() {
    const { signOnPopover } = this.state;
    const anchor = signOnPopover && document.querySelector(`[data-sign-on-anchor="${signOnPopover.anchorId}"]`);
    this.setState({ signOnPopover: null });
    // Return focus to the bar (the anchor's parent) for keyboard users.
    if (anchor && anchor.parentElement) {
      anchor.parentElement.focus();
    }
  }

  handleActivityFormModalOk(id, item) {
    const {
      activities, updateRosterLocalAssignments, onProcessTags, match,
    } = this.props;
    const { selectedActivity } = this.state;
    const { params } = match;
    const rosterVersionId = params.id;
    blockNavigation(true);
    const activity = activities.find(a => a.id === item.activity.key);
    // The form edits a LOCAL value under its own key; store the UTC value on the item.
    const { available_sign_on_local: signOnLocal, ...formItem } = item;
    const signOnActivity = activity || (selectedActivity.activity && selectedActivity.activity.id === item.activity.key
      ? selectedActivity.activity
      : null);
    const data = {
      ...formItem,
      activity,
      available_sign_on: isOta(signOnActivity) ? toUtcValue(signOnLocal || null, item.start_time) : null,
      tags: []
    };

    onProcessTags(THEME_KEY.assignment, id, item.tags, selectedActivity.tags, rosterVersionId)
      .then((response) => {
        if (response) {
          updateRosterLocalAssignments([{
            ...data,
            tags: unFormatTags(item.tags)
          }]);
        }
        else {
          updateRosterLocalAssignments([{
            ...data,
            tags: unFormatTags(selectedActivity.tags)
          }]);
        }
      });
    message.success('Assignment updated');
  }

  handleTravelModalOk(id, item) {
    const { travel_items, updateRosterTravelAssignment } = this.props;
    blockNavigation(true);

    const travel = travel_items.find(a => a.id === `t-${item.id}`);

    if (travel) {
      const data = {
        ...travel,
        notes: item.notes
      };
      updateRosterTravelAssignment(data);
      message.success('Travel updated');
    }
  }

  handleRowContextClick = (e, row, clickedTime, snappedClickedTime) => {
    const { clipboard } = this.props;
    const { timelineSettings } = this.state;
    const isUserPivot = rosterPivot.getGroup(timelineSettings.options) === rosterPivot.PIVOT_TYPES.USER;
    if (!clipboard || this.isReadOnly() || !isUserPivot) {
      return;
    }
    e.preventDefault();
    this.setState({
      pasteMenu: {
        visible: true,
        x: e.clientX,
        y: e.clientY,
        anchorRowId: row,
        anchorTime: snappedClickedTime ? snappedClickedTime.valueOf() : clickedTime.valueOf()
      }
    });
  };

  closePasteMenu = () => {
    this.setState(prev => ({ pasteMenu: { ...prev.pasteMenu, visible: false } }));
  };

  handlePasteMenuClick = ({ key }) => {
    const { dispatchPasteAssignments, dispatchClearClipboard, match } = this.props;
    const { pasteMenu } = this.state;
    if (key === 'paste') {
      blockNavigation(true);
      dispatchPasteAssignments(Number(pasteMenu.anchorRowId), pasteMenu.anchorTime, match.params.id);
    }
    else if (key === 'clear') {
      dispatchClearClipboard();
    }
    this.closePasteMenu();
  };

  handleDocumentClickForPasteMenu = () => {
    const { pasteMenu } = this.state;
    if (pasteMenu.visible) {
      this.closePasteMenu();
    }
  };

  handleRowMouseMove = (row, snappedTime) => {
    this.lastCursorPos = {
      anchorRowId: row,
      anchorTime: snappedTime ? snappedTime.valueOf() : null
    };
  };

  // True when a keyboard shortcut should be ignored: read-only, activity pivot,
  // or focus is in an editable field (so native copy/paste is preserved).
  shortcutBlocked(e) {
    const { timelineSettings } = this.state;
    const isUserPivot = rosterPivot.getGroup(timelineSettings.options) === rosterPivot.PIVOT_TYPES.USER;
    const tag = (e.target && e.target.tagName) || '';
    const inEditable = /input|textarea/i.test(tag) || (e.target && e.target.isContentEditable);
    return !e.ctrlKey && !e.metaKey ? true : (this.isReadOnly() || !isUserPivot || inEditable);
  }

  handleCopyKeydown = (e) => {
    if (this.shortcutBlocked(e)) {
      return;
    }
    const { dispatchCopyAssignments, selectedAssignments, match } = this.props;
    if (!selectedAssignments.length) {
      return;
    }
    dispatchCopyAssignments(selectedAssignments, match.params.id);
  };

  handlePasteKeydown = (e) => {
    if (this.shortcutBlocked(e)) {
      return;
    }
    const { dispatchPasteAssignments, match } = this.props;
    const { anchorRowId, anchorTime } = this.lastCursorPos;
    if (anchorRowId == null || anchorTime == null) {
      return;
    }
    blockNavigation(true);
    dispatchPasteAssignments(Number(anchorRowId), anchorTime, match.params.id);
  };

  handleActivityFormModalCancel() {
    this.setState({
      showActivityForm: false,
      selectedActivity: null
    });
  }

  handleItemContextClick(event) {
    const { key } = event;
    const { timelineSettings } = this.state;

    const {
      dispatchSelectTimelineActivities, loadTags, user_groups, activity_groups, local_items, travel_items, server_items, deleted_items, leave_items, selectedAssignments
    } = this.props;

    const pivotType = rosterPivot.getGroup(timelineSettings.options);
    let items = [];

    if (pivotType === rosterPivot.PIVOT_TYPES.USER) {
      items = rosterPivot.getUserGroupItems(
        user_groups, local_items, server_items, deleted_items, travel_items, leave_items
      );
    }
    else {
      items = rosterPivot.getActivityGroupItems(activity_groups, local_items, server_items, deleted_items);
    }
    const activity = items.find(item => item.id === event.item.props.data.key);

    if (selectedAssignments.length === 1 && activity) {
      dispatchSelectTimelineActivities([]);
    }
    // Show the Edit Activity dialog for the selected item
    if (activity) {
      switch (key) {
      case 'edit':
        loadTags(activity.id);
        this.setState({
          showActivityForm: true,
          selectedActivity: {
            ...activity,
            tags: formatTags(activity.tags)
          }
        });
        break;

      case 'editselected':
        if (local_items.filter(item => item.id < 0).length) {
          message.warn('Assignments must be saved before bulk editing');
        }
        else {
          this.setState({
            showBulkActivityForm: true
          });
        }
        break;

      case 'reassign':
        this.setState({
          showReassignForm: true,
          ...(selectedAssignments.length < 2) && {
            selectedActivity: {
              ...activity,
              tags: formatTags(activity.tags)
            }
          }
        });
        break;

      case 'action': {
        const { resetActionType } = this.props;
        resetActionType();
        // default object
        const data = {
          theme: '',
          object: {}
        };

        // setup theme objects
        if (Object.keys(event.item.props.data)
          .indexOf('leave') > 0) {
          data.theme = 'userleave';
          data.object = { id: event.item.props.data.leave.id.replace('l-', '') };
        }
        else if (Object.keys(event.item.props.data)
          .indexOf('booking') > 0) {
          data.theme = 'booking';
          data.object = { id: event.item.props.data.booking.id.replace('t-', '') };
        }
        else {
          data.theme = 'assignment';
          data.object = { id: event.item.props.data.key };
        }
        // update state
        this.setState({
          showActionForm: true,
          contextItem: {
            ...data
          }
        });
        break;
      }

      case 'travel': {
        this.setState({
          showTravelInput: true,
          selectedActivity: {
            ...activity,
            tags: formatTags(activity.tags)
          }
        });
        break;
      }

      case 'copy': {
        if (this.isReadOnly()) {
          break;
        }
        const { dispatchCopyAssignments, match } = this.props;
        const idsToCopy = selectedAssignments.length ? selectedAssignments : [activity.id];
        dispatchCopyAssignments(idsToCopy, match.params.id);
        break;
      }

      case SIGN_ON_TASK: {
        const ids = selectedAssignments.length > 1 ? selectedAssignments : [activity.id];
        const anchor = document.querySelector(`[data-sign-on-anchor="${activity.id}"]`);
        const bar = anchor && anchor.parentElement;
        const box = bar ? bar.getBoundingClientRect() : {
          top: 0, left: 0, width: 0, height: 0
        };
        this.setState({
          signOnPopover: {
            anchorId: activity.id,
            ids,
            rect: {
              top: box.top, left: box.left, width: box.width, height: box.height
            }
          }
        });
        break;
      }

      default:
        break;
      }
    }
  }

  handleSingleMouseClick(e, key) {
    // Update the user selection
    if (Number.isNaN(key)) {
      return;
    }
    const { timelineSettings } = this.state;
    if (!timelineSettings.options.rosterPivoted) {
      const { dispatchSelectTimelineActivities, selectedAssignments } = this.props;
      const assignmentList = toggleListSelection(selectedAssignments, key);
      dispatchSelectTimelineActivities(assignmentList);
    }
  }

  handleItemClicks(e, key) {
    if (e.button !== MOUSE_RIGHT_CLICK) { // if user clicked anything other than the right
      this.handleSingleMouseClick(e, key);
    }
  }

  handleRowClick(e, key) {
    // if there is an intentional row click
    if (key) {
      const { dispatchSelectTimelineActivities } = this.props;
      dispatchSelectTimelineActivities();
    }
  }

  handleRowDoubleClick(e, rowNumber, clickedTime, snappedClickedTime) {
    const { selectedToolbarItem, timelineSettings } = this.state;
    if (rosterPivot.getGroup(timelineSettings.options) !== rosterPivot.PIVOT_TYPES.USER) {
      return;
    }

    const { user_groups, dispatchAddTimelineActivity } = this.props;

    if (selectedToolbarItem) {
      blockNavigation(true);
      const group = _.find(user_groups, g => g.id === Number(rowNumber));
      switch (selectedToolbarItem.type) {
      case 'activity': {
        const activity = selectedToolbarItem.data;
        dispatchAddTimelineActivity(
          activity,
          group,
          snappedClickedTime,
          timelineSettings.options.activityCreateCount,
          timelineSettings.options.activityHoursBetween,
          timelineSettings.options.timelineMaxDays
        );
        break;
      }
      case 'template': {
        const template = selectedToolbarItem.data;
        this.addTemplate(template, user_groups, Number(rowNumber), snappedClickedTime);
        break;
      }
      default:
        break;
      }
    }
    else {
      message.warning('No activity or template selected');
    }
  }

  handleGroupClick(group) {
    this.setState({
      userForm: {
        visible: true,
        userId: group.uid
      }
    });
  }

  handleUserFormModalClose() {
    this.setState({
      userForm: {
        visible: false,
        userId: null
      }
    });
  }

  handleGroupPin(group) {
    const { match, dispatchToggleGroupPin } = this.props;
    const { params } = match;
    const rosterVersionId = params.id;
    if (group.uid > 0) {
      dispatchToggleGroupPin(group);
      this.loadRosterTimeline(rosterVersionId, false);
    }
  }

  /**
   * Updates the list of pinned items in roster timeline store.
   * @param items New list of items to be saved in store.
   * @param itemType Identifier for `items`, e.g. 'activity', 'template'
   */
  handlePinItem(items, itemType) {
    const { dispatchUpdatePinnedActivities } = this.props;

    switch (itemType) {
      case 'activity': {
        dispatchUpdatePinnedActivities(items);
        break;
      }
      default:
        break;
    }
  }

  handleReassignModalCancel() {
    this.setState({
      showReassignForm: false,
      selectedActivity: null
    });
  }

  handleActionFormModalOk() {
    const { formInput } = this.actionForm;
    const { saveAction, clearAction } = this.props;
    const { contextItem } = this.state;

    // check to see the form ref exists
    if (formInput) {
      const form = formInput.getForm();
      form.validateFields((errors, values) => {
        if (_.isEmpty(errors)) {
          const {
            requestor, assignees, tags, context_theme, status, priority, action_type, notification_types, ...rest
          } = values;

          const data = {
            action_type_id: action_type.key,
            requestor_id: requestor.key,
            status_id: status.key,
            priority_id: priority.key,
            notification_type_id_list: notification_types.map(type => type.key),
            context_theme_id: context_theme.key,
            context_object_id: contextItem.object.id,
            assignee_id_list: assignees.map(type => type.key),
            user_tag_id_list: tags && tags.length ? tags.map(type => type.key) : [],
            ...rest
          };
          saveAction(data)
            .then((createdAction) => {
              message.success('Action successfully created');
              this.setState({
                showActionForm: false
              });
              clearAction();
              // Update the assignment dot immediately without requiring a roster refresh
              if (createdAction && contextItem.theme === 'assignment') {
                const hasSms = createdAction.notification_types && createdAction.notification_types.some(nt => nt.resourcetype === 'SMSConfig');
                if (hasSms) {
                  const { dispatchUpdateAssignmentAction } = this.props;
                  dispatchUpdateAssignmentAction(
                    contextItem.object.id,
                    {
                      action_type_name: createdAction.action_type && createdAction.action_type.name,
                      notification_type_names: createdAction.notification_types.map(nt => nt.name).join(', '),
                      description: createdAction.description,
                      // Send is async; mark pending so the popover shows "Sending..."
                      // until the next roster load resolves the real outcome.
                      sms_sent_timestamp: null,
                      sms_send_failed: false,
                      sms_send_pending: true
                    },
                    createdAction.status && !createdAction.status.is_resolved
                  );
                }
              }
            })
            .catch(() => {
              message.error('Action could not be created');
            });
        }
      });
    }
  }

  handleActionFormModalCancel() {
    this.setState({
      showActionForm: false,
      selectedActivity: null
    });
  }

  handleBulkAssignmentModalCancel() {
    this.setState({
      showBulkActivityForm: false
    });
  }

  handleTimelineInteraction(type, changes, items) {
    const { dispatchMoveTimelineActivities, dispatchResizeTimelineActivities, dispatchSelectTimelineActivities } = this.props;
    const { timelineSettings } = this.state;
    const { selectedAssignments, user_groups } = this.props;

    switch (type) {
    case Timeline.changeTypes.dragStart: {
      return selectedAssignments;
    }

    case Timeline.changeTypes.dragEnd: {
      dispatchMoveTimelineActivities(changes, items, timelineSettings.options.timelineMaxDays, user_groups);
      blockNavigation(true);
      break;
    }

    case Timeline.changeTypes.resizeStart: {
      return selectedAssignments;
    }

    case Timeline.changeTypes.resizeEnd: {
      dispatchResizeTimelineActivities(changes, items, timelineSettings.options.timelineMaxDays);
      blockNavigation(true);
      break;
    }

    case Timeline.changeTypes.itemsSelected: {
      this.setState({ rosterItemsInFlight: true });
      if (!timelineSettings.options.rosterPivoted) {
        const activities = changes.map(item => item.key)
          .filter(k => !Number.isNaN(Number(k)));
        dispatchSelectTimelineActivities(activities);
      }
      this.setState({ rosterItemsInFlight: false });
      break;
    }

    default:
      return changes;
    }
    return changes;
  }

  requireRedraw() {
    if (this.timeline9000RequiresRedraw) {
      this.timeline9000RequiresRedraw = false;
      return true;
    }
    return false;
  }

  unselectAssignments(id) {
    const { dispatchSelectTimelineActivities, selectedAssignments } = this.props;
    const selectedItems = selectedAssignments.filter(i => i !== id);
    dispatchSelectTimelineActivities(selectedItems);
    if (!selectedItems.length) {
      this.setState({
        showBulkActivityForm: false
      });
    }
  }

  renderGroupTitle() {
    const { timelineSettings } = this.state;
    const { user_groups } = this.props;
    const extraUsers = user_groups.length > MAX_USERS;
    const title = rosterPivot.getGroupTitle(rosterPivot.getGroup(timelineSettings.options), this.props, extraUsers);

    return (
      <div className="bhp-group-title">
        {title}
      </div>
    );
  }

  renderToolbar(showToolbar, isLoading, isPublished, noChanges) {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const { match, permissions } = this.props;
    const { params } = match;
    const rosterVersionId = params.id;
    const enableSettings = this.sidebar && (this.sidebar.userFilter || this.sidebar.timelineOptions);

    return (
      <ToolBar>
        <ToolBar.Right>
          <Tooltip title="Pan back" placement="bottom">
            <Button size="small" icon="left" loading={isLoading} onClick={this.handlePanBack} />
          </Tooltip>
          <DatePicker.RangePicker
            allowClear={false}
            value={[visibleTimeStart, visibleTimeEnd]}
            format="DD-MM-YYYY H:mm"
            size="small"
            showTime={{ format: 'HH:mm' }}
            onChange={(e) => {
              this.setState({
                visibleTimeStart: e[0],
                visibleTimeEnd: e[1]
              }, () => {
                // loadRosterTimeline already re-requests the assignments for the new window;
                // the loadRosterUserAssignments call that used to follow it was a duplicate.
                this.loadRosterTimeline(rosterVersionId);
              });
            }}
          />
          <Tooltip title="Pan forwards" placement="bottom">
            <Button size="small" icon="right" loading={isLoading} onClick={this.handlePanForward} />
          </Tooltip>
          <Tooltip title="Zoom out" placement="bottom">
            <Button size="small" icon="minus" loading={isLoading} onClick={this.handleZoomOut} />
          </Tooltip>
          <Tooltip title="Zoom in" placement="bottom">
            <Button size="small" icon="plus" loading={isLoading} onClick={this.handleZoomIn} />
          </Tooltip>
          {permissions.CanAdminRosters()
            .hasPermissions() && (
            <Tooltip title="Validate" placement="bottom">
              <Button
                className="validateButton"
                type="primary"
                size="small"
                icon="carry-out"
                disabled={isLoading || !noChanges}
                loading={isLoading}
                onClick={() => this.setState({ showValidations: true })}
              >
                Validations
              </Button>
            </Tooltip>
          )}
          <Counters rosterId={rosterVersionId} start={visibleTimeStart} end={visibleTimeEnd} />
          {permissions.CanAdminRosters()
            .hasPermissions() && (
            <Tooltip title="Publish" placement="bottom">
              <Button
                className="publishButton"
                type="primary"
                size="small"
                icon="safety"
                disabled={isPublished || isLoading}
                loading={isLoading}
                onClick={this.handlePublishRoster}
              >
                {!noChanges ? 'Save & Publish' : 'Publish'}
              </Button>
            </Tooltip>
          )}
          {permissions.CanAdminRosters()
            .hasPermissions() && (
            <Tooltip title="Save" placement="bottom">
              <Button
                className="saveButton"
                type="primary"
                size="small"
                icon="save"
                disabled={noChanges || isLoading}
                loading={isLoading}
                onClick={this.handleSaveRoster}
              >
                Save
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Refresh" placement="bottom">
            <Button size="small" icon="reload" disabled={isLoading} loading={isLoading} onClick={this.handleRefreshRoster}>
              {!noChanges ? 'Discard & Refresh' : 'Refresh'}
            </Button>
          </Tooltip>
          {permissions.CanAdminRosters()
            .hasPermissions() && (
            <Tooltip title="Timeline Settings" placement="bottom">
              <Button
                className="bhp-button-settings"
                size="small"
                icon="setting"
                disabled={!enableSettings || isLoading}
                loading={isLoading}
                onClick={this.handleShowSettings}
              >
                Settings
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Show Options" placement="bottom">
            <Button size="small" icon={showToolbar ? 'menu-unfold' : 'menu-fold'} onClick={this.handleShowToolbar}>
              {showToolbar ? 'Hide options' : 'Show options'}
            </Button>
          </Tooltip>
        </ToolBar.Right>
      </ToolBar>
    );
  }

  renderItem(props) {
    const { timelineSettings } = this.state;
    const {
      selectedAssignments, user_groups, local_items, server_items
    } = this.props;
    return (
      <ItemRenderer
        {...props}
        titleFormat={timelineSettings.options.assignmentTitleFormat}
        pivotType={rosterPivot.getGroup(timelineSettings.options)}
        onContextClick={this.handleItemContextClick}
        timeFormat="Do MMM HH:mm"
        hiddenContextItems={[...(selectedAssignments.length > 1 ? ['edit', 'action', 'travel'] : ['editselected'])]}
        selectedItems={this.memoSelectedItems(selectedAssignments, user_groups, local_items, server_items)}
      />
    );
  }

  renderGroup({ group, timelineExtras }) {
    if (!group || typeof group.uid === 'undefined') {
      return null;
    }
    const { timelineSettings, visibleTimeStart, visibleTimeEnd } = this.state;
    const {
      loadUserTags, clearUser, loadUser, permissions
    } = this.props;
    const pivotType = rosterPivot.getGroup(timelineSettings.options);
    const isUser = pivotType === rosterPivot.PIVOT_TYPES.USER;
    const isClickable = group.uid !== -1 && isUser && permissions.CanViewUsers()
      .hasPermissions();

    const callback = (data, visible) => {
      if (visible && isUser && data.uid > 0) {
        loadUser(data.uid);
        loadUserTags(data.uid, visibleTimeStart.format('YYYY-MM-DDTHH:mm:ssZ'), visibleTimeEnd.format('YYYY-MM-DDTHH:mm:ssZ'));
      }
      else {
        clearUser();
      }
    };

    return (
      <GroupRenderer
        rowIndex={group.id.toString()}
        title={group.title}
        data={{
          ...group
        }}
        isUser={isUser}
        isActive={group.active}
        isClickable={isClickable}
        isDraggable={false}
        showDelete={false}
        showPin={group.uid !== -1 && isUser}
        isPinned={group.pinNumber != null}
        mouseEnterDelay={0.1}
        onClick={this.handleGroupClick}
        onPin={this.handleGroupPin}
        onChangeCallback={callback}
        timelineExtras={timelineExtras}
      />
    );
  }

  render() {
    const {
      showBulkActivityForm,
      selectedToolbarItem,
      selectedActivity,
      contextItem,
      showToolbar,
      showTemplateMapper,
      showActionForm,
      showActivityForm,
      showReassignForm,
      showValidations,
      userForm,
      timelineSettings,
      rosterItemsInFlight,
      visibleTimeStart,
      visibleTimeEnd,
      timelineMode,
      showTravelInput,
      customFilters,
      pasteMenu,
      signOnPopover
    } = this.state;

    const {
      match,
      local_items,
      server_items,
      travel_items,
      leave_items,
      deleted_items,
      availability_items,
      user_groups,
      activity_groups,
      permissions,
      selectedAssignments,
      rosterVersion,
      rosterVersionInFlight,
      usersInFlight,
      activityInFlight,
      pinnedActivities,
    } = this.props;

    const { params } = match;
    const rosterVersionId = params.id;

    const selectedToolbarItemId = selectedToolbarItem ? selectedToolbarItem.data.id : null;
    const noChanges = !local_items.length && !deleted_items.length;
    const isLoading = rosterVersionInFlight || rosterItemsInFlight || usersInFlight || activityInFlight;
    const pivoted = timelineSettings.options.rosterPivoted;

    const timelineToolbarTabs = {
      showActivities: !pivoted && permissions.CanAdminRosters()
        .hasPermissions(),
      showTemplates: !pivoted && permissions.CanAdminRosters()
        .hasPermissions(),
      showFilters: true,
      showOptions: true,
      optionFields: {
        showActivityCreateCount: !pivoted,
        showActivityHoursBetween: !pivoted,
        showTimelineSnapMinutes: !pivoted,
        showTimelineMaxDays: false,
        showDisplayTravelLayer: !pivoted,
        showDisplayLeaveLayer: !pivoted,
        showDisplayAvailabilityLayer: !pivoted,
        showRosterPivot: true,
        showHideEmptyRows: true,
        showAssignmentTitleFormat: true,
        showTimeFilter: true,
        showHideUnallocatedRows: true
      }
    };

    const pivotType = rosterPivot.getGroup(timelineSettings.options);
    const groups = rosterPivot.getRosterGroups(pivotType, user_groups, activity_groups);

    const items = this.deriveRosterItems(
      pivotType, groups, local_items, server_items, deleted_items, travel_items, leave_items, this.getValidationErrors()
    );

    const timelineItems = this.deriveTimelineItems(items, timelineSettings.options, customFilters.activity);

    const layers = this.deriveAvailabilityLayers(availability_items, user_groups, visibleTimeStart, visibleTimeEnd);
    const timelineLayers = rosterPivot.getLayers(pivotType, layers);
    const selectedItems = this.deriveSelectedItems(items, selectedAssignments);

    // Force a react-timeline-9000 redraw when the rendered items, the visible window,
    // OR the current selection change. TimelineBody.shouldComponentUpdate only shallow-
    // compares height/width/rowCount, so a selection-only change does NOT repaint the
    // grid on its own — without the selectedItems check below, clicked/drag-selected
    // items never get the selected class (regression from the items/date-only guard).
    // selectedItems is memoized (deriveSelectedItems), so its identity is stable except
    // when the selection or the underlying items actually change.
    if (timelineItems !== this.lastTimelineItems
      || visibleTimeStart !== this.lastVisibleTimeStart
      || visibleTimeEnd !== this.lastVisibleTimeEnd
      || selectedItems !== this.lastSelectedItems) {
      this.timeline9000RequiresRedraw = true;
      this.lastTimelineItems = timelineItems;
      this.lastVisibleTimeStart = visibleTimeStart;
      this.lastVisibleTimeEnd = visibleTimeEnd;
      this.lastSelectedItems = selectedItems;
    }

    const selectedTravelItem = selectedActivity && typeof selectedActivity.id === 'string' && selectedActivity.id.indexOf('t') !== -1;
    // R19b: compute once per render; guarded so getSignOnTargets is never called without signOnPopover.
    const signOnTargets = signOnPopover ? this.getSignOnTargets() : [];
    return (
      <div id="RosterTimeline" className="bhp-roster-timeline bhp-container">
        <KeyHandler keyEventName="keydown" keyCode={DELETE_KEY_CODE} onKeyHandle={this.handleDeleteKeydown} />
        <KeyHandler keyEventName="keydown" keyCode={BACKSPACE_KEY_CODE} onKeyHandle={this.handleDeleteKeydown} />
        <KeyHandler keyEventName="keyup" keyCode={CTRL_KEY_CODE} onKeyHandle={this.handleCtrlKeyup} />
        <KeyHandler keyEventName="keydown" keyCode={CTRL_KEY_CODE} onKeyHandle={this.handleCtrlKeydown} />
        <KeyHandler keyEventName="keydown" keyCode={C_KEY_CODE} onKeyHandle={this.handleCopyKeydown} />
        <KeyHandler keyEventName="keydown" keyCode={V_KEY_CODE} onKeyHandle={this.handlePasteKeydown} />
        <HeaderBar title={breadcrumbs(rosterVersion)} toolbar={this.renderToolbar(showToolbar, isLoading, rosterVersion.is_published, noChanges)} />
        <SlidePanel direction="right" width={365} show={showToolbar}>
          <Sidebar
            tabs={timelineToolbarTabs}
            defaultSettings={rosterVersion.default_settings || defaultSettings}
            options={timelineSettings.options}
            config={{
              pickers,
              filters
            }}
            onItemChange={this.handleToolbarItemChange}
            onFilterChange={this.handleCustomFilters}
            onOptionsChange={this.handleOptionsChange}
            onPinItem={this.handlePinItem}
            pinnedActivities={pinnedActivities}
            visibleTimeStart={visibleTimeStart}
            visibleTimeEnd={visibleTimeEnd}
            ref={this.getSidebar}
          />
          <Spin spinning={isLoading} indicator={<Icon type="loading" spin />} tip="Loading...">
            <div className="bhp-timeline">
              <Timeline
                shallowUpdateCheck
                forceRedrawFunc={this.requireRedraw}
                showCursorTime
                cursorTimeFormat="D MMM YYYY HH:mm"
                groups={groups}
                groupTitleRenderer={this.renderGroupTitle}
                groupRenderer={this.renderGroup}
                groupOffset={250}
                items={timelineItems}
                itemRenderer={this.renderItem}
                selectedItems={selectedAssignments}
                rowLayers={timelineLayers}
                timelineMode={this.isReadOnly() ? 1 : timelineMode}
                startDate={visibleTimeStart}
                endDate={visibleTimeEnd}
                snapMinutes={Number(timelineSettings.options.timelineSnapMinutes)}
                onItemClick={(e, key) => this.handleItemClicks(e, key)}
                onRowClick={this.handleRowClick}
                onRowDoubleClick={this.handleRowDoubleClick}
                onRowContextClick={this.handleRowContextClick}
                onRowMouseMove={this.handleRowMouseMove}
                onInteraction={this.handleTimelineInteraction}
              />
              {pasteMenu.visible && (
                <div
                  className="bhp-paste-context-menu"
                  style={{
                    position: 'fixed',
                    top: pasteMenu.y,
                    left: pasteMenu.x,
                    zIndex: 1050
                  }}
                  onMouseLeave={this.closePasteMenu}
                >
                  <Menu className="bhp-context-menu" selectable={false} onClick={this.handlePasteMenuClick}>
                    <Menu.Item key="paste">
                      <span className="bhp-context-menu-item">
                        <Icon type="snippets" />
                        Paste here
                      </span>
                    </Menu.Item>
                    <Menu.Item key="clear">
                      <span className="bhp-context-menu-item">
                        <Icon type="delete" />
                        Clear clipboard
                      </span>
                    </Menu.Item>
                  </Menu>
                </div>
              )}
              {signOnTargets.length > 0 && (
                <SignOnPopover
                  items={signOnTargets}
                  rect={signOnPopover.rect}
                  presets={getOtPresets()}
                  onApply={this.handleSignOnApply}
                  onClose={this.handleSignOnClose}
                />
              )}
            </div>
          </Spin>
        </SlidePanel>
        <Drawer
          placement="right"
          width={1200}
          closable
          maskClosable
          visible={showValidations}
          destroyOnClose
          onClose={() => this.setState({ showValidations: false })}
          className="bhp-drawer bhp-drawer-edit"
          title="Validations"
        >
          <ValidationTasks
            rosterId={Number(rosterVersionId)}
            title="Validation Task"
            canAdmin
            visibleTimeStart={visibleTimeStart}
            visibleTimeEnd={visibleTimeEnd}
          />
        </Drawer>
        <Modal
          title="Add Template to Roster"
          wrapClassName="bhp-modal-form bhp-vertical-centered bhp-fullscreen"
          visible={showTemplateMapper}
          closable={false}
          destroyOnClose
          okText="Add to Roster"
          onOk={this.handleTemplateMapperModalOk}
          onCancel={this.handleTemplateMapperModalCancel}
        >
          <TemplateMapper
            ref={this.getTemplateMapperRef}
            templateId={selectedToolbarItemId}
            rosterDateRange={[visibleTimeStart, visibleTimeEnd]}
          />
        </Modal>
        {selectedTravelItem && (
          <Drawer
            placement="right"
            width={1200}
            closable
            maskClosable
            visible={showTravelInput}
            destroyOnClose
            onClose={this.handleTravelInputCancel}
            className="bhp-drawer bhp-drawer-edit"
            title="Travel Notes"
          >
            <TravelInput
              canAdmin
              isEditMode
              ref={this.getTravelFormRef}
              dataSource={{ ...selectedActivity, ...(selectedActivity) && { id: selectedActivity.id.replace('t-', '') } }}
              title="Travel"
              onSaveClick={this.handleTravelModalOk}
              onCancelClick={this.handleTravelInputCancel}
            />
          </Drawer>
        )}
        <Drawer
          placement="right"
          width={1200}
          closable
          maskClosable
          visible={showActivityForm}
          destroyOnClose
          onClose={this.handleActivityFormModalCancel}
          title="Edit Assignment"
          className="bhp-drawer bhp-drawer-edit"
        >
          <Tabs
            defaultActiveKey="1"
          >
            <TabPane tab="Detail" key="1">
              {selectedActivity && (
                <ActivityInput
                  canAdmin
                  isEditMode
                  ref={this.getActivityFormRef}
                  dataSource={selectedActivity}
                  title="Assignment"
                  picker="datetime"
                  showNotes
                  showAdminNotes={permissions.CanAdminRosters().hasPermissions()}
                  showSignOn
                  onSaveClick={this.handleActivityFormModalOk}
                  onCancelClick={this.handleActivityFormModalCancel}
                  themeName="assignment"
                />
              )}
            </TabPane>
            <TabPane tab="Notifications" key="2" disabled={selectedActivity && selectedActivity.id < 0}>
              {selectedActivity && (
                <Notifications
                  isRun
                  objectId={selectedActivity.id}
                  onDelete={this.handleNotificationDelete}
                  onSave={this.handleNotifications}
                  onDeleteCallback={this.handlePostNotificationDelete}
                  permissions={permissions}
                  reducers={{
                    item: REDUCER_KEY.notificationRun,
                    list: REDUCER_KEY.notificationRuns
                  }}
                  themeName="assignment"
                  title="Notification"
                />
              )}
            </TabPane>
            <TabPane tab="History" key="3" disabled={selectedActivity && selectedActivity.id < 0}>
              {selectedActivity && (
                <AuditHistory
                  objectId={selectedActivity.id}
                  themeName={THEME_NAMES.assignment}
                />
              )}
            </TabPane>
          </Tabs>
        </Drawer>
        <Modal
          title="Actions"
          wrapClassName="bhp-modal-form bhp-vertical-centered"
          visible={showActionForm}
          closable={false}
          destroyOnClose
          onOk={this.handleActionFormModalOk}
          onCancel={this.handleActionFormModalCancel}
          okText="Save"
          cancelText="Cancel"
          width="720px"
        >
          <ActionInput
            commands={{
              showSaveButton: false,
              showCancelButton: false
            }}
            title="Activity Action"
            ref={this.getActionFormRef}
            item={contextItem.object}
            theme={contextItem.theme}
            canAdmin={permissions.CanAdminActions()
              .hasPermissions()}
          />
        </Modal>
        <Modal
          title="View User"
          wrapClassName="bhp-modal-form bhp-vertical-centered bhp-fullscreen"
          visible={userForm.visible}
          closable
          destroyOnClose
          onCancel={this.handleUserFormModalClose}
          footer={[
            <Button key="submit" type="primary" onClick={this.handleUserFormModalClose}>
              Close
            </Button>
          ]}
        >
          <UserAccount showCancelButton={false} userId={userForm.userId} />
        </Modal>
        <Modal
          title={`Reassign ${selectedActivity ? 1 : selectedAssignments.length} ${selectedActivity || selectedAssignments.length === 1 ? 'Activity' : 'Activities'}`}
          visible={showReassignForm}
          wrapClassName="bhp-modal-form bhp-vertical-centered"
          closable
          destroyOnClose
          okText="Reassign"
          onOk={this.handleUserReassign}
          onCancel={this.handleReassignModalCancel}
          width="720px"
        >
          <ReassignActivities wrappedComponentRef={this.getUserReassignRef} />
        </Modal>
        <Drawer
          placement="right"
          width={1200}
          closable
          maskClosable
          visible={showBulkActivityForm}
          destroyOnClose
          onClose={this.handleBulkAssignmentModalCancel}
          title="Edit Bulk Assignments"
          className="bhp-drawer bhp-drawer-bulk"
        >
          <Tabs
            defaultActiveKey="1"
          >
            <TabPane tab="Detail" key="1">
              <ActivitiesPanel
                title="Bulk Activities"
                itemRenderer={(it, tf) => activityItemContent(it, tf, { showSignOn: true })}
                selectedObjects={selectedAssignments}
                selectedItems={selectedItems}
                onSave={this.handleBulkActivitySave}
                onCancel={this.handleBulkAssignmentModalCancel}
                onDeselect={this.unselectAssignments}
                showAdminNotes={permissions.CanAdminRosters().hasPermissions()}
              />
            </TabPane>
            <TabPane tab="Notifications" key="2">
              <NotificationsPanel
                headerTitle="Selected Assignments"
                itemRenderer={(it, tf) => activityItemContent(it, tf, { showSignOn: true })}
                items={items}
                onCancel={this.handleBulkActivityModalCancel}
                onDeselect={this.unselectAssignments}
                onSave={this.handleBulkNotifications}
                onDelete={this.handleNotificationBulkDelete}
                reducer={REDUCER_KEY.notificationRun}
                removeText="Remove all notifications from selected assignments"
                selectedObjects={selectedAssignments}
                selectedItems={selectedItems}
                themeName="assignment"
                title="Notification"
              />
            </TabPane>
          </Tabs>
        </Drawer>
        <NavigationPrompt when={isNavigationBlocked()}>
          {(isOpen, onConfirm, onCancel) => (
            <Modal
              visible={isOpen}
              onOk={onCancel}
              onCancel={onConfirm}
              title="Unsaved Changes"
              okText="No - Remain here"
              cancelText="Yes - Discard Changes"
              maskClosable={false}
              closable={false}
            >
              <div>
                <p>
                  You have made changes to the timeline that have not been saved.
                </p>
                <p>
                  Do you wish to discard these changes?
                </p>
              </div>
            </Modal>
          )}
        </NavigationPrompt>
      </div>
    );
  }
}

RosterTimeline.propTypes = {
  // General props
  match: PropTypes.shape({
    params: PropTypes.object,
    router: PropTypes.object,
    route: PropTypes.string
  }).isRequired,
  location: PropTypes.object.isRequired,
  // Reducer State
  activities: PropTypes.array,
  notifications: PropTypes.array,
  server_items: PropTypes.array,
  local_items: PropTypes.array,
  deleted_items: PropTypes.arrayOf(PropTypes.number),
  travel_items: PropTypes.array,
  leave_items: PropTypes.array,
  user_groups: PropTypes.array,
  activity_groups: PropTypes.array,
  availability_items: PropTypes.array,
  selectedAssignments: PropTypes.array,
  rosterVersion: PropTypes.object.isRequired,
  userFilterInFlight: PropTypes.bool,
  rosterVersionInFlight: PropTypes.bool,
  usersInFlight: PropTypes.bool,
  activityInFlight: PropTypes.bool,
  pinnedActivities: PropTypes.array,
  // Reducer Dispatch
  loadRosterVersion: PropTypes.func.isRequired,
  loadUserList: PropTypes.func.isRequired,
  loadActivityList: PropTypes.func.isRequired,
  loadRosterServerAssignments: PropTypes.func.isRequired,
  clearRosterLocalAssignments: PropTypes.func.isRequired,
  saveRosterLocalAssignments: PropTypes.func.isRequired,
  updateRosterLocalAssignments: PropTypes.func.isRequired,
  checkCompetencies: PropTypes.func.isRequired,
  clipboard: PropTypes.object,
  dispatchCopyAssignments: PropTypes.func.isRequired,
  dispatchPasteAssignments: PropTypes.func,
  dispatchClearClipboard: PropTypes.func,
  dispatchSelectTimelineActivities: PropTypes.func.isRequired,
  dispatchSetTimelineGroups: PropTypes.func.isRequired,
  dispatchAddRosterEventDetails: PropTypes.func.isRequired,
  dispatchDeleteTimelineActivity: PropTypes.func.isRequired,
  dispatchAddTimelineActivity: PropTypes.func.isRequired,
  dispatchToggleGroupPin: PropTypes.func.isRequired,
  dispatchMoveTimelineActivities: PropTypes.func.isRequired,
  dispatchResizeTimelineActivities: PropTypes.func.isRequired,
  dispatchUpdatePinnedActivities: PropTypes.func.isRequired,
  // Other
  onSaveSettings: PropTypes.func,
  loadActionContext: PropTypes.func,
  saveAction: PropTypes.func,
  dispatchUpdateAssignmentAction: PropTypes.func,
  resetActionType: PropTypes.func,
  permissions: PropTypes.object.isRequired,
  clearAction: PropTypes.func,
  removeNotifications: PropTypes.func,
  onProcessTags: PropTypes.func,
  loadTags: PropTypes.func,
  loadUser: PropTypes.func,
  clearUser: PropTypes.func,
  swapTimelineAssignments: PropTypes.func,
  loadUserTags: PropTypes.func,
  validationTasks: PropTypes.array,
  loadValidationTasks: PropTypes.func,
  updateRosterTravelAssignment: PropTypes.func
};

RosterTimeline.defaultProps = {
  activities: [],
  notifications: [],
  clipboard: null,
  server_items: [],
  local_items: [],
  deleted_items: [],
  travel_items: [],
  leave_items: [],
  user_groups: [],
  activity_groups: [],
  availability_items: [],
  selectedAssignments: [],
  userFilterInFlight: false,
  rosterVersionInFlight: false,
  usersInFlight: false,
  activityInFlight: false,
  pinnedActivities: [],
  onSaveSettings() {
  },
  loadActionContext() {
  },
  saveAction() {
  },
  dispatchUpdateAssignmentAction() {
  },
  resetActionType() {
  },
  clearAction() {
  },
  removeNotifications() {
  },
  onProcessTags() {
  },
  loadTags() {
  },
  loadUser() {
  },
  clearUser() {
  },
  swapTimelineAssignments() {
  },
  loadUserTags() {
  },
  validationTasks: [],
  loadValidationTasks() {
  },
  updateRosterTravelAssignment() {
  },
  dispatchPasteAssignments() {
  },
  dispatchClearClipboard() {
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(RosterTimeline);

